import axios, { AxiosResponse, AxiosError } from 'axios';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { notifySuccess, notifyError, toastMessage } from '@helpers/index';
import { translateOutsideReact } from '@shared/i18n/intlInstance';
import { fetchSubscriptionsRequest } from '@modules/subscriptions/core/actions';

import {
  fetchAddressesRequest,
  fetchAddressesSuccess,
  fetchAddressesFailure,
  updateAddressRequest,
  updateAddressSuccess,
  updateAddressFailure,
  createAddressRequest,
  createAddressSuccess,
  createAddressFailure,
} from './actions';
import {
  FETCH_ADDRESSES_REQUEST,
  UPDATE_ADDRESS_REQUEST,
  CREATE_ADDRESS_REQUEST,
} from './actionTypes';
import { IAddress } from './types';

type LoopErrorBody = {
  message?: string;
  code?: string;
  data?: { message?: string; code?: string } | null;
};

const extractLoopError = (e: unknown): { message?: string; code?: string } => {
  const body = (e as AxiosError<LoopErrorBody>)?.response?.data;
  return {
    message: body?.data?.message || body?.message,
    code: body?.data?.code || body?.code,
  };
};

const addressErrorToast = (e: unknown) => {
  const { message, code } = extractLoopError(e);
  const text = (message || '').toLowerCase();

  // A saga cannot call useIntl, so copy is resolved through the intl the provider
  // publishes. The second argument is the German fallback used only if a toast
  // fires before the provider's first render.
  if (text.includes('zip') || text.includes('postal') || code === 'SHOPIFY_ERROR') {
    return toastMessage(
      translateOutsideReact(
        'ADDRESS.ERROR.ZIP.TITLE',
        'Die Postleitzahl passt nicht zum gewählten Land.',
      ),
      translateOutsideReact(
        'ADDRESS.ERROR.ZIP.TEXT',
        'Bitte aktualisiere Adresse, Stadt und Postleitzahl passend zum neuen Land und versuche es erneut.',
      ),
    );
  }

  if (text.includes('duplicate')) {
    return toastMessage(
      translateOutsideReact(
        'ADDRESS.ERROR.DUPLICATE.TITLE',
        'Diese Adresse ist bereits in deinem Konto gespeichert.',
      ),
      translateOutsideReact(
        'ADDRESS.ERROR.DUPLICATE.TEXT',
        'Bitte ändere mindestens ein Feld oder wähle die vorhandene Adresse aus der Liste aus.',
      ),
    );
  }

  return toastMessage(
    translateOutsideReact(
      'ADDRESS.ERROR.GENERIC.TITLE',
      'Deine Adresse konnte nicht aktualisiert werden.',
    ),
    translateOutsideReact(
      'ADDRESS.ERROR.GENERIC.TEXT',
      'Bitte überprüfe deine Eingaben und versuche es erneut.',
    ),
  );
};

const getAddresses = () => axios.get<IAddress>(`/api/v1/addresses`);

const updateAddress = async (id: number, params: any) => {
  const response = await axios.put<IAddress>(`/api/v1/addresses/${id}`, params);
  return response.data;
};

const createAddress = async (params: any) => {
  const response = await axios.post<any>(`/api/v1/addresses/`, params);
  return response.data;
};

function* fetchAddressesSaga() {
  try {
    const response: AxiosResponse<IAddress> = yield call(getAddresses);

    yield put(
      fetchAddressesSuccess({
        addresses: response.data,
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchAddressesFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* updateAddressSaga(action: ReturnType<typeof updateAddressRequest>) {
  try {
    const response: AxiosResponse<IAddress> = yield call(() =>
      updateAddress(action.payload.id, action.payload.params),
    );

    yield put(
      updateAddressSuccess({
        address: response.data,
      }),
    );

    yield put(fetchSubscriptionsRequest());

    notifySuccess(translateOutsideReact('ADDRESS.TOAST.UPDATED', 'Versandadresse aktualisiert'));
  } catch (e: unknown) {
    const { message } = extractLoopError(e);
    yield put(
      updateAddressFailure({
        error: message || (e instanceof Error ? e.message : 'Unknown error'),
      }),
    );
    notifyError(addressErrorToast(e));
  }
}

function* createAddressSaga(action: ReturnType<typeof createAddressRequest>) {
  try {
    const response: AxiosResponse<IAddress> = yield call(() =>
      createAddress(action.payload.params),
    );

    yield put(
      createAddressSuccess({
        address: response.data,
      }),
    );

    yield put(fetchAddressesRequest());

    notifySuccess(translateOutsideReact('ADDRESS.TOAST.CREATED', 'Neue Versandadresse erstellt'));
  } catch (e: unknown) {
    const { message } = extractLoopError(e);
    yield put(
      createAddressFailure({
        error: message || (e instanceof Error ? e.message : 'Unknown error'),
      }),
    );
    notifyError(addressErrorToast(e));
  }
}

export function* addressesSaga() {
  yield all([
    takeLatest(FETCH_ADDRESSES_REQUEST, fetchAddressesSaga),
    takeLatest(UPDATE_ADDRESS_REQUEST, updateAddressSaga),
    takeLatest(CREATE_ADDRESS_REQUEST, createAddressSaga),
  ]);
}
